import { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { SimpleDialog } from 'src/components/modal';
import { getProductImages } from 'src/components/widget/lineupWidget/editProductPanel/EditProductPanel.helper';
import { PATHS } from 'src/constants/paths';
import { useGetDyTemplates, useGetProduct } from 'src/hooks';
import { lang, useUserRole } from 'src/utils';

import { VariationTemplateMappingStep } from '../addVariationTemplatePage/modules/variationTemplateMappingStep/VariationTemplateMappingStep';
import { type VariationMap } from '../addVariationTemplatePage/modules/variationTemplateMappingStep/VariationTemplateMappingStep.utils';
import { EditVariationTemplatePageWrapper } from './EditVariationTemplatePage.styled';
import { useEditVariationTemplatePageHandlers } from './hooks/useEditVariationTemplatePage.hook';
import { useUnloadTempData } from './hooks/useUnloadTempData';

export const EditVariationTemplatePage = () => {
  const { templateId, productCode = '', variationCode = '' } = useParams();
  const { isViewOnly } = useUserRole();
  const { data: templates = [], isLoading: isTemplatesLoading } =
    useGetDyTemplates(true);
  const { data: product, isLoading: isProductLoading } =
    useGetProduct(productCode);

  const [variationMap, setVariationMap] = useState<VariationMap>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { shouldRemoveItemRef } = useUnloadTempData(productCode);

  const selectedTemplate = templates.find((t) => t.id === Number(templateId));

  const {
    discardModal,
    handleDiscard,
    handleDiscardConfirm,
    handleVariationTemplateNext,
    initialVariationMap,
    isVariationMapDirty,
  } = useEditVariationTemplatePageHandlers({
    productCode,
    variationCode,
    selectedTemplate,
    product,
    isProductLoading,
    shouldRemoveItemRef,
    variationMap,
    isSubmitting,
    setIsSubmitting,
  });

  useEffect(() => {
    if (
      Object.keys(variationMap).length === 0 &&
      Object.keys(initialVariationMap).length > 0
    ) {
      setVariationMap(initialVariationMap);
    }
  }, [initialVariationMap, variationMap]);

  const isLoading = isTemplatesLoading || (isProductLoading && !product);

  return isViewOnly ? (
    <Navigate to={PATHS.NOT_FOUND} replace />
  ) : (
    <EditVariationTemplatePageWrapper>
      {isLoading ? null : (
        <VariationTemplateMappingStep
          template={selectedTemplate!}
          variationMap={variationMap}
          setVariationMap={setVariationMap}
          mainProductAssetName={
            getProductImages(product, variationCode)?.[0]?.name || ''
          }
          onDiscard={handleDiscard}
          onNext={handleVariationTemplateNext}
          isVariationMapDirty={isVariationMapDirty}
          isSaving={isSubmitting}
        />
      )}

      {discardModal.open && (
        <SimpleDialog
          modalSize="sm"
          open={discardModal.open}
          onClose={discardModal.onCloseModal}
          onClickCancel={handleDiscardConfirm}
          onClickConfirm={discardModal.onCloseModal}
          title={lang.cancelDySetupModal.title}
          subTitle={lang.cancelDySetupModal.subTitle}
          cancelText={lang.navigateBlockerModal.confirmText}
          confirmText={lang.navigateBlockerModal.cancelText}
        />
      )}
    </EditVariationTemplatePageWrapper>
  );
};
